import { useId, useState } from "react";
import { useTranslation, type TFunction } from "react-i18next";
import {
  IconArticle,
  IconBooks,
  IconBulb,
  IconChevronDown,
  IconClipboardSearch,
  IconFileSearch,
  IconFileText,
  IconInfoCircle,
  IconListSearch,
  IconMessageQuestion,
  IconMessageReply,
  IconScribble,
  type IconSearch,
  IconStack2,
  IconUsersGroup,
  IconWorldSearch,
} from "@tabler/icons-react";
import PubmedLogo from "../../assets/icons/pubmed-logo.svg?react";
import { cn } from "../../lib/utils";
import type { GptAction } from "../../domains/gpt/transcript";

// Gap between two rows entering. Deliberately shorter than the 140ms each row
// takes, so the cascade overlaps into one motion instead of reading as a queue:
// eight rows are fully in under 320ms.
const STAGGER_STEP_MS = 25;

/**
 * The line between a question and its answer, and the record of how the answer
 * was reached (Figma "ai-search-dropdown" 6690:7243, desktop sibling
 * "Desktop/ReasoningTrace"). It replaces the older `AnswerSourcesRow`: the
 * header carries the same "n sources" entry point into the Sources tab, plus
 * how many searches ran and how long the turn took, and the chevron opens the
 * ordered trace of what the agent actually did.
 *
 * The trace is drawn from the turn's REAL tool calls (`GptAction`), framed by
 * the two phases every turn has whether or not it searched: reading the
 * question, and writing the answer. Nothing else is invented — a general turn
 * that never screened anything by level of evidence does not claim to have,
 * which in a medical product is the difference between a record and a story.
 */
export function ChatActionsDropdown({
  variant,
  actions,
  searchCount,
  sourceCount,
  durationLabel,
  onOpenSources,
  className,
}: {
  // `desktop`: the trace as the desktop mockups draw it. `default`: the
  // phone's, left as it was.
  variant: TraceVariant;
  // The agent's calls, in the order it made them. Empty on a turn that answered
  // from what it already had — the header then shows sources alone and there is
  // nothing to expand.
  actions: ReadonlyArray<GptAction>;
  // Retrieval calls only (`gptSearchCount`) — the suggestion tools are traced
  // but are not searches.
  searchCount: number;
  // Null while the answer streams: its sources arrive with its end.
  sourceCount: number | null;
  // Already-localized "25s", or null when the turn's duration is unknown (a
  // blocking Evidence turn, which is not timed, or a turn still streaming).
  durationLabel: string | null;
  onOpenSources: () => void;
  className?: string;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const traceId = useId();

  return (
    <div className={cn("flex flex-col gap-2 py-1", className)}>
      <div className="flex h-6 items-center justify-between">
        {/* The words "n sources" are the entry point into the Sources tab, so
            they are the button that opens it. The chevron beside them owns the
            trace: two affordances, each doing what it says, rather than one row
            that has to guess which of the two a tap meant. */}
        {(searchCount > 0 || sourceCount !== null) && (
          <button
            type="button"
            onClick={onOpenSources}
            className="flex cursor-pointer items-center gap-1 bg-transparent p-0 text-left font-dm-sans text-footnote text-ds-slate-900"
          >
            {searchCount > 0 && (
              <span>{t("{{count}} searches", { count: searchCount })}</span>
            )}
            {searchCount > 0 && sourceCount !== null && (
              <span aria-hidden="true">·</span>
            )}
            {sourceCount !== null && (
              <span>{t("{{count}} sources", { count: sourceCount })}</span>
            )}
          </button>
        )}
        {/* A turn with no recorded call has no trace to disclose — a chevron
            opening onto "read the question, wrote the answer" would promise a
            record and deliver a truism. The duration still shows: it is a fact
            about the turn, not about the trace. */}
        {actions.length === 0 ? (
          durationLabel !== null && (
            <span className="ml-auto font-dm-sans text-footnote text-ds-slate-500">
              {durationLabel}
            </span>
          )
        ) : (
          <button
            type="button"
            onClick={() => {
              setIsOpen(!isOpen);
            }}
            aria-expanded={isOpen}
            aria-controls={traceId}
            aria-label={t("What the agent did")}
            className="-m-1 ml-auto flex cursor-pointer items-center gap-2 bg-transparent p-1 font-dm-sans text-footnote text-ds-slate-500"
          >
            {durationLabel !== null && <span>{durationLabel}</span>}
            {/* One glyph that turns, not two that swap: the rotation is the
                animation, and it points at the list it just opened. */}
            <IconChevronDown
              stroke={1.75}
              className={cn(
                "size-4 transition-transform duration-150 ease-out",
                isOpen && "rotate-180"
              )}
            />
          </button>
        )}
      </div>
      {isOpen && actions.length > 0 && (
        <ChatTrace
          variant={variant}
          id={traceId}
          actions={actions}
          answer="started"
        />
      )}
    </div>
  );
}

/**
 * The ordered trace of one turn, framed by its two phases: what the dropdown
 * opens onto, and the desktop context panel's Progression section.
 */
export function ChatTrace({
  variant,
  id,
  actions,
  answer,
}: {
  variant: TraceVariant;
  // For the `aria-controls` of the control that discloses it, or null.
  id: string | null;
  actions: ReadonlyArray<GptAction>;
  // The writing shows once it has started, never as a promise.
  answer: "started" | "pending";
}): React.JSX.Element {
  const { t } = useTranslation();
  // The two bookends are phases, not tool calls, so they are rendered here
  // rather than carried in the data: every turn reads its question and writes
  // its answer, and no tool records either.
  const rows: ReadonlyArray<{
    icon: TraceIcon;
    label: string;
    wraps: boolean;
  }> = [
    {
      icon: { kind: "line", glyph: IconClipboardSearch },
      label:
        variant === "desktop"
          ? t("Understanding the question")
          : t("Analyzing the question"),
      wraps: false,
    },
    ...actions.map((action) => ({
      icon: actionIcon(action.kind),
      label: actionLabel(t, action),
      // On desktop a query wraps like a sentence (Figma "steps · terminées"):
      // cut short, the line would hide what was actually searched. The phone
      // wraps the sentences only and truncates a query.
      wraps: variant === "desktop" ? true : isSentence(action),
    })),
    ...(answer === "started"
      ? [
          {
            icon: { kind: "line", glyph: IconScribble } as const,
            label: t("Writing the answer"),
            wraps: false,
          },
        ]
      : []),
  ];

  return (
    <div id={id === null ? undefined : id} className="flex flex-col">
      {rows.map((row, index) => (
        <TraceRow
          key={index}
          index={index}
          tone={variant === "desktop" ? "settled" : "phone"}
          {...row}
        />
      ))}
    </div>
  );
}

type TraceVariant = "default" | "desktop";

// A line glyph, or the PubMed mark: it keeps its brand colors so it stays
// recognizable (as in `GPTToolStep`), so it takes no stroke.
type TraceIcon =
  { kind: "line"; glyph: typeof IconSearch } | { kind: "pubmed" };

export function TraceGlyph({
  icon,
  stroke,
  className,
}: {
  icon: TraceIcon;
  stroke: number;
  className: string;
}): React.JSX.Element {
  switch (icon.kind) {
    case "line": {
      const Glyph = icon.glyph;
      return <Glyph stroke={stroke} className={className} />;
    }
    case "pubmed":
      return <PubmedLogo aria-hidden className={className} />;
    default:
      throw new Error(icon satisfies never);
  }
}

// The reading of the question and its assumptions: sentences, not queries.
function isSentence(action: GptAction): boolean {
  return action.kind === "question" || action.kind === "assumption";
}

function TraceRow({
  index,
  icon,
  label,
  wraps,
  tone,
}: {
  index: number;
  icon: TraceIcon;
  label: string;
  wraps: boolean;
  // `settled`: a finished turn's record on desktop. `phone`: the phone's record, left as it was.
  tone: "phone" | "settled";
}): React.JSX.Element {
  return (
    <div
      className="flex animate-trace-in flex-col"
      // Per-position, so it cannot be a class — there is no utility for
      // "the 4th row" (same reason `GPTPrePrompts` does this inline).
      // The rows arrive top-down, which is the order the agent worked
      // in, so the reveal reads as the trace being replayed.
      style={{ animationDelay: `${String(index * STAGGER_STEP_MS)}ms` }}
    >
      {/* The connector hangs from the icon's centre: 8px of left inset
          plus half of the 16px glyph. */}
      {index > 0 && (
        <span
          className={cn(
            "ml-4 h-2 w-px",
            tone === "phone" ? "bg-ds-black/48" : "bg-ds-slate-500"
          )}
        />
      )}
      <div
        className={cn(
          "flex min-w-0 gap-1.25 pl-2",
          tone === "phone" ? "text-ds-black/48" : "text-ds-slate-500",
          wraps ? "items-start" : "items-center"
        )}
      >
        <TraceGlyph
          icon={icon}
          stroke={tone === "phone" ? 1.75 : 1.25}
          className={cn("size-4 shrink-0", wraps && "mt-0.5")}
        />
        <span
          className={cn(
            "font-dm-sans text-footnote",
            wraps ? "wrap-break-word" : "truncate"
          )}
        >
          {label}
        </span>
      </div>
    </div>
  );
}

function actionLabel(t: TFunction, action: GptAction): string {
  const query = action.query;
  switch (action.kind) {
    case "search":
      return query === null
        ? t("Searching Juisci, PubMed and the web")
        : t("Searching Juisci, PubMed and the web: {{query}}", { query });
    case "library":
      return query === null
        ? t("Searching your Juisci library")
        : t("Searching your Juisci library: {{query}}", { query });
    // Each call names its source: a page read is not a search.
    case "pubmed":
      return query === null
        ? t("Searching PubMed")
        : t("Searching PubMed: {{query}}", { query });
    case "web":
      return query === null
        ? t("Searching the clinical web")
        : t("Searching the clinical web: {{query}}", { query });
    case "similar":
      return query === null
        ? t("Finding similar articles")
        : t("Finding similar articles: {{query}}", { query });
    case "read":
      return query === null
        ? t("Reading the article")
        : t("Reading the article: {{query}}", { query });
    case "files":
      return query === null
        ? t("Searching your files")
        : t("Searching your files: {{query}}", { query });
    case "fileRead":
      return t("Reading your file");
    case "suggestTopics":
      return t("Looking for topics to follow");
    case "suggestRooms":
      return t("Looking for a Room on the subject");
    case "question":
      return query === null
        ? t("Question understood")
        : t("Question understood: {{query}}", { query });
    case "assumption":
      return query === null
        ? t("Assumption")
        : t("Assumption: {{query}}", { query });
    case "clarification":
      return query === null
        ? t("Question clarified")
        : t("Question clarified: {{query}}", { query });
    default:
      throw new Error(action.kind satisfies never);
  }
}

// One icon per kind of step, none shared, so a step reads at a glance.
export function actionIcon(kind: GptAction["kind"]): TraceIcon {
  switch (kind) {
    case "pubmed":
      return { kind: "pubmed" };
    case "search":
      return { kind: "line", glyph: IconListSearch };
    case "library":
      return { kind: "line", glyph: IconBooks };
    case "web":
      return { kind: "line", glyph: IconWorldSearch };
    case "similar":
      return { kind: "line", glyph: IconStack2 };
    case "read":
      return { kind: "line", glyph: IconArticle };
    case "files":
      return { kind: "line", glyph: IconFileSearch };
    case "fileRead":
      return { kind: "line", glyph: IconFileText };
    case "suggestTopics":
      return { kind: "line", glyph: IconBulb };
    case "suggestRooms":
      return { kind: "line", glyph: IconUsersGroup };
    case "question":
      return { kind: "line", glyph: IconMessageQuestion };
    case "assumption":
      return { kind: "line", glyph: IconInfoCircle };
    case "clarification":
      return { kind: "line", glyph: IconMessageReply };
    default:
      throw new Error(kind satisfies never);
  }
}
